import { Component, Vue, Watch } from "vue-property-decorator";
import DataTable, { TableVisibility } from "@/components/data-table/DataTable";
import { AxiosError } from "axios";
import ErrorHandler from "@/support/ErrorHandler";
import EditDialog from "@/views/Variants/EditDialog";
import { Size } from "@/models/Size";
import { Color } from "@/models/Color";
import { Brand } from "@/models/Brand";
import { Supplier } from "@/models/Supplier";
import { Category } from "@/models/Category";
import { formatDate } from "@/support/String";
import UpdateEanWithCsv from '@/views/Variants/UpdateEanWithCsv';
import UploadProductImageDialog from "@/views/Variants/UploadProductImageDialog";
import UploadProductCsvDialog from "@/views/Variants/UploadProductCsvDialog";
import ProductAttributesDialog from "@/views/Variants/ProductAttributesDialog";
import BulkUpdateProducts from "@/views/Variants/BulkUpdateProducts";
import MiFileUpload from "@/components/mi-file-upload/MiFileUpload";
import { ProductVariantFilterItem } from "@/components/CustomFilters/VariantFilters/VariantFilters";
import RenderFlow from "@/support/RenderFlow";
import { Options } from "@/components/mi-dialog/MiDialog";
import debounce from "debounce";

import { ProductVariant, ProductVariantIncludes } from '@/models/ProductVariant';
import { Product, ProductImage } from '@/models/Product';
import { axiosDownload } from '@/support/Client';
import { Season } from '@/models/Season';
import { thumbnailTransformer } from "@/support/ThumbnailTransformer";

@Component<Variants>({
  components: {
    EditDialog,
    UpdateEanWithCsv,
    UploadProductImageDialog,
    UploadProductCsvDialog,
    ProductAttributesDialog,
    MiFileUpload,
    BulkUpdateProducts,
  }
})
export default class Variants extends Vue {
  public $pageTitle = "Variants";

  protected productDetailDialog: boolean = false;

  protected imageDialog: boolean = false;

  protected isExportingProducts: boolean = false;
  protected editProductDialog: boolean = false;
  protected isDisplayingQuickAddProductsDialog = false;
  protected isUpdatingEanWithCsv = false;
  protected isUploadingImages = false;

  protected RenderFlow = new RenderFlow([
    "hasMultipleSelectedProducts",
    "isUpdatingBulk",
    "isUploadingImages",
    "isUploadingCSV",
    "isViewingAttributes",
  ]);

  protected variants: ProductVariant[] = [];

  protected orderItems: any[] = [];

  protected selectedProductVariant: ProductVariant | null = null;
  protected selectedProduct: Product | null = null;

  protected totalPages: number = 1;

  protected page: number = 1;

  protected totalProducts: number = 0;

  protected selectedApprovedStatus: boolean = false;

  protected productComment: string = "";

  protected approvedOptions: approvedOption[] = [
    {
      label: "Ok",
      value: true
    },
    {
      label: "Niet ok",
      value: false
    }
  ];

  protected items: string[] = [];

  protected orderUserId: string = "";

  protected selectedProductVariants: any = [];

  protected selectedOrder: any = null;

  protected uploadedFiles: any[] = [];

  protected currentIndex: number = 0;

  protected searchInput: string = "";

  protected selectedImage: string = "";

  protected isGettingFilters = true;

  protected isLoadingFilters = true;

  protected isShowingFilters = false;

  protected filtersHeight = 0;

  protected limit = 25;

  // Note, also change the defaults in getProductFilters() ...
  protected productFilter: ProductVariantFilterItem = {
    out_of_stock: "0",
    no_images: "0",
    withoutAttributes: "0",
    status: [],
    product:'',
    seasons: [],
    all_variants: '1',
    block_date_from: '',
    block_date_until: '',
    min_price: '',
    max_price: '',
    final_return_date_from: '',
    final_return_date_until: '',
    attributes: [],
  };

  protected visibility: TableVisibility = {
    search: true,
    total: false,
    limit: true,
    pagination: true,
    checkboxes: true,
    title: false,
  };

  public async mounted() {
    this.$root.$emit("breadcrumbUpdated", {
      crumb: [{ name: `Products` }]
    });

    this.page = this.getPage();
    this.limit = this.getLimit();
    await this.setFilters();
    this.isGettingFilters = false;
  }

  protected async getProduct(productId: string): Promise<void> {
    this.selectedProduct = await new Product()
    .find(productId)
  }

  protected async exportProducts(): Promise<void> {
    this.isExportingProducts = true;
    let payload: exportPayload = {};

    if (this.selectedProductVariants.length) {
      payload.product_variants = (this.selectedProductVariants.map((product: ProductVariant) => product.id) as string[]);
    };

    try {
      await axiosDownload({
        url: new Product().exportProducts(),
        accept: 'text/csv; charset=utf-8'
      }, payload);
    } catch (error) {
      ErrorHandler.network(error);
    }
    this.isExportingProducts = false;
  }

  protected async setFilters(): Promise<void> {
    const storedSessionFilters = await this.getProductFilters();

    if (!storedSessionFilters) {
      return;
    }

    this.productFilter = storedSessionFilters as ProductVariantFilterItem;
  }

  public getProductFilters() {
    const filters = localStorage.getItem(`${this.$route.name}_productFilters`);

    if (filters) {
      var parsedobj = JSON.parse(filters)

      // All variants is always on the variants page, and never on the webshop page
      // Set the default values
      parsedobj.all_variants = '1';
      if (!("out_of_stock" in parsedobj)) {
        parsedobj.out_of_stock = this.productFilter.out_of_stock;
      }

      if (!("no_images" in parsedobj)) {
        parsedobj.no_images = this.productFilter.no_images;
      }

      if (!("withoutAttributes" in parsedobj)) {
        parsedobj.withoutAttributes = this.productFilter.withoutAttributes;
      }

      return parsedobj;
    }

    return this.productFilter;
  }

  protected handleDataTableClick(event: Event) {
    if (!event.target) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target.getAttribute("data-image-large")) {
      this.selectedImage = target.getAttribute("data-image-large") as string;
      this.imageDialog = true;
    }
  }

  protected setPage(page: number) {
    localStorage.setItem(`${this.$route.name}_page`, page.toString());
  }

  protected getPage() {
    const result = localStorage.getItem(`${this.$route.name}_page`);

    if (result) {
      return Number(result);
    }

    return 1;
  }

  protected getLimit() {
    const result = localStorage.getItem(`${this.$route.name}_limit`);

    if (result) {
      return Number(result);
    }

    return this.limit;
  }

  protected handleFilterChanged(filter: ProductVariantFilterItem) {
    this.productFilter = { ...filter };
    // Do not reset the page on reload
    if (! this.isGettingFilters) {
      this.page = 1;
      this.setPage(1);
    }
  }

  protected showFilters(): void {
    if (! this.isShowingFilters) {
      this.isShowingFilters = true;
      const innerFilters = document.getElementsByClassName('inner-filters');
      this.filtersHeight = innerFilters[0].clientHeight;
    } else {
      this.isShowingFilters = false;
      this.filtersHeight = 0;
    }
  }

  public handleSearch() {
    return debounce((searchInput: string) => {
      // Reset to page 1 on searching
      this.page = 1;
      this.showFilters();
    }, 400);
  }

  protected onUpdateEanWithCsvSuccess(): void {
    this.refresh();
  }

  get tableOptions() {
    const sourceMap: any = {
      Products: {
        name: {
          singular: "Product",
          plural: "Producten"
        },
        sort: {
          key: "created_at",
          order: "DESC"
        },
        headers: [
          {
            text: '',
            width: '85px',
            value: 'image_url',
            transform: (image: string) => image ? thumbnailTransformer(image) : '',
          },
          {
            text: `<span title="Product Name / EAN / SKU / Supplier Style name">Product Name</span>`,
            value: 'product_name',
            action: '/varianten/{id}',
            sortable: {
              key: 'product_name',
              order: 'DESC'
            },
            transform: (name: string, variant: ProductVariant) => {
              return `<div class='custom-name'>
                      ${ variant.product_name ? `<span><strong>${variant.product_name}</strong></span>` : ''}
                      ${ variant.name ? `<span><strong>${variant.name}</strong></span>` : ''}
                      ${ variant.upc ? `<span>${variant.upc}</span>` : ''}
                      ${ variant.sku ? `<span class='text-grey'>${variant.sku}</span>` : ''}
                      ${ variant.supplier_style_name ? `<span class='text-light-grey'>${variant.supplier_style_name}</span>` : ''}
                      </div>
                      `;
            }
          },
          {
            text: 'Product ID',
            value: 'product_id',
          },
          {
            text: 'Supplier / Brand',
            value: 'brand',
            transform: (value: Brand, productVariant: ProductVariant) => {
              const brand = (productVariant.brand as Brand);
              const supplier = (productVariant.supplier as Supplier);
              return `${brand && brand.name ? brand.name : ''} <br> ${supplier && supplier.name ? supplier.name : ''}`;
            }
          },
          {
            text: 'Category',
            value: 'category',
            sortable: {
              key: 'category',
              order: 'DESC'
            },
            transform: (name: string, product: ProductVariant) => {
              if (product.hasOwnProperty('categories')) {
                return (product.categories as Category[])
                  .map(categoryObj => categoryObj.name)
                  .sort()
                  .join(', ');
              } else {
                return '';
              }
            }
          },
          {
            text: 'Status',
            value: 'status',
            transform: (status: string) => {
              return `<span class='statusPill statusPill--${status}'>${status}</span>`;
            }
          },
          {
            text: 'Color / Size',
            value: 'brand',
            transform: (value: Brand, productVariant: ProductVariant) => {
              const color = (productVariant.color as Color);
              const size = (productVariant.size as Size);
              return `${color && color.name ? color.name : ''} <br> ${size && size.name ? size.name : ''}`;
            }
          },
          {
            text: 'Supplier Color / Size',
            value: 'brand',
            transform: (value: Brand, productVariant: ProductVariant) => {
              return `${productVariant.supplier_color} <br> ${productVariant.supplier_size}`;
            }
          },
          {
            text: 'Season',
            value: 'season',
            transform: (season: Season) => {
              return season && season.name ? season.name : '';
            }
          },
          {
            text: 'Price',
            value: 'created_at',
            transform: (date: string, productVariant: ProductVariant) => {
              return `<div class="text-right">
                      <span class="d-block white-space-no-wrap">R: € ${productVariant.retail_price}</span>
                      <span class="d-block white-space-no-wrap">B: € ${productVariant.buy_price}</span>
                      </div>
                      `;
            },
          },
          {
            text: 'Attr',
            value: 'attributes_count',
            action: (productVariant: ProductVariant) => {
              if (!productVariant || !productVariant.attributes_count) {
                return;
              }

              if (productVariant.attributes_count > 0 ) {
                this.selectedProductVariant = productVariant;
                this.RenderFlow.set("isViewingAttributes", true);

              }
            },
            transform: (count: number) => {
              return `<span class="d-block">${count > 0 ? count : '0'}</span>`;
            }
          },
          {
            text: 'Stock',
            value: 'stock',
            transform: (stock: string, productVariant: ProductVariant) => {
              return `<div class="text-right">
                      <span class="d-block white-space-no-wrap">OH: ${productVariant.stock_on_hand} </span>
                      <span class="d-block white-space-no-wrap">AV:  ${stock}</span>
                      <span class="d-block white-space-no-wrap">PO: ${productVariant.stock_purchase_order} </span>
                      <span class="d-block white-space-no-wrap">MQ: ${productVariant.minimum_stock_quantity} </span>
                      </div>
                      `;
            },
          },
          {
            text: 'Is Rerun',
            value: 'is_rerun',
            transform: (is_rerun: boolean) => is_rerun ? 'Yes' : 'No',
          },
          {
            text: 'Is Dummy upc',
            value: 'is_dummy_upc',
            transform: (is_dummy_upc: boolean) => is_dummy_upc ? 'Yes' : 'No',
          },
          {
            text: 'Created / Block / Return',
            value: 'created_at',
            sortable: {
              key: 'created_at',
              order: 'DESC'
            },
            transform: (date: string, product: ProductVariant) => {
              return `<div>
                      <span class="d-block"><b>Last received:</b> ${product.last_received_at && product.last_received_at !== '0000-00-00 00:00:00' ? formatDate(product.last_received_at as string, 'dd-LL-yyyy') : 'Not set'}</span>
                      <span class="d-block"><b>Created:</b> ${formatDate(date, 'dd-LL-yyyy')}</span>
                      <span class="d-block"><b>Blocked:</b> ${product.block_date && product.block_date !== '0000-00-00 00:00:00' ? formatDate(product.block_date as string, 'dd-LL-yyyy') : 'Not set'}</span>
                      <span class="d-block"><b>Final Return:</b> ${product.final_return_date && product.final_return_date !== '0000-00-00 00:00:00' ? formatDate(product.final_return_date as string, 'dd-LL-yyyy') : 'Not set'}</span>
                      </div>
                      `;
            },
          },
        ],
        filters: [
          //
        ],
        actions: [
          {
            type: "add",
            label: "add",
            icon: "collections",
            action: (vm: any, product: ProductVariant) => {
              this.selectedProductVariant = product;
              this.isUploadingImages = true;
            },
            tooltip: "Upload foto's"
          },
          {
            type: 'family',
            label: 'family',
            icon: 'people',
            action: async (vm: any, variant: ProductVariant) => {
              if (!variant.product_uuid) {
                return;
              }

              this.selectedProductVariant = variant;
              await this.getProduct(variant.product_uuid);
              this.isDisplayingQuickAddProductsDialog = true;
            },
            tooltip: 'Clone'
          },
          {
            type: "view",
            label: "view",
            icon: "remove_red_eye",
            action: '/varianten/{id}',
            tooltip: "Bekijk variant"
          }
        ],
        model: new ProductVariant()
          .include([
            ProductVariantIncludes.SEASON,
            ProductVariantIncludes.CATEGORIES,
            ProductVariantIncludes.ATTRIBUTES_COUNT,
            ProductVariantIncludes.COLOR,
            ProductVariantIncludes.BRAND,
            ProductVariantIncludes.SUPPLIER,
            ProductVariantIncludes.SIZE,
          ])
          .filter(this.productFilter)
      }
    };
    return sourceMap["Products"];
  }

  protected setApprovedStatus(product: ProductVariant) {
    if (product && product.is_approved) {
      this.selectedApprovedStatus = true;
    } else {
      this.selectedApprovedStatus = false;
    }
  }

  protected closeImageDialog() {
    if (this.imageDialog) {
      this.imageDialog = false;
    }
  }

  protected closeDialog() {
    if (this.editProductDialog) {
      this.editProductDialog = false;
    }
  }

  protected isReturningProducts() {
    this.$store.dispatch("openDialog", this.dialogReturingProducts);
  }

  get dialogReturingProducts(): Options {
    return {
      title: this.$t("dialogOptions.confirmation").toString(),
      text:
        "Weet je zeker dat je de geselecteerde producten wilt retourneren naar de leverancier?",
      type: "warning",
      buttons: {
        confirm: {
          text: "Ok",
          action: () => {
            this.returnProducts();
          }
        },
        cancel: {
          text: this.$t("dialogOptions.button.cancel").toString(),
          action: () => {}
        }
      }
    };
  }

  protected returnProducts() {
    new ProductVariant()
      .returnToSupplier({
        product_variants: this.selectedProductVariants
          ? this.selectedProductVariants.map((product: any) => {
              return product.id;
            })
          : []
      })
      .then((order: any) => {
        this.selectedOrder = order;
        this.$store.dispatch("openDialog", this.dialogReturingProductsSuccess);
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  get dialogReturingProductsSuccess(): Options {
    return {
      title: this.$t("dialogOptions.confirmation").toString(),
      text: "Er is een order aangemaakt met de geselecteerde producten.",
      type: "success",
      buttons: {
        confirm: {
          text: "Ga naar order",
          action: () => {
            this.$router.push(
              `/webshop/order/${this.selectedOrder.order}/sets`
            );
          }
        },
        cancel: {
          text: this.$t("dialogOptions.button.close").toString(),
          action: () => {
            this.selectedProductVariants = [];
            this.variantsDatatable
              ? this.variantsDatatable.clearSelectBoxes()
              : null;
          }
        }
      }
    };
  }

  protected get variantsDatatable(): DataTable | null {
    const dataTable = this.$refs.variantsDatatable;

    if (dataTable) {
      return dataTable as DataTable;
    }

    return null;
  }

  protected colorCheckboxChanged(color: Color) {}

  public refresh() {
    this.selectedProductVariants = [];
    if (this.variantsDatatable) {
      this.variantsDatatable.unselectAll();
      this.variantsDatatable.refresh();
    }
  }

  @Watch("editProductDialog")
  protected editProductDialogChanged() {
    if (!this.editProductDialog) {
      this.selectedProductVariant = null;
    }
  }

  @Watch("selectedProductVariants")
  protected countSelectedProducts() {
    this.RenderFlow.set(
      "hasMultipleSelectedProducts",
      this.selectedProductVariants && this.selectedProductVariants.length > 0
    );
  }

  @Watch("page")
  public pageChange(value: any, oldValue: any) {
    this.setPage(value);
  }
}

export interface approvedOption {
  label: string;
  value: boolean;
}

interface attributeFilter {
  attribute?: string;
  option?: string;
}

interface exportPayload  {
  product_variants?: string[];
}
